import { getApiBaseUrl, requestJson } from "@/lib/api";

export type LabelWeightUnit = "g" | "kg" | "ml" | "un";

/** Formato da etiqueta: completa (50x112mm, com nutricional) ou compacta (50x30mm, só nome + datas). */
export type LabelFormat = "FULL" | "COMPACT";

export interface LabelGenerateInput {
  productId: string;
  /** Nome a imprimir na etiqueta; quando omitido, usa o nome cadastrado do produto. */
  nameOverride?: string;
  lote?: string;
  fabricadoEm?: string; // yyyy-MM-dd
  validoAte?: string; // yyyy-MM-dd
  netWeightGrams?: number;
  /** Unidade do conteúdo líquido: "g" (padrão), "kg" ou "un". */
  netWeightUnit?: LabelWeightUnit;
  portionSizeGrams?: number;
  quantity?: number;
  /** Formato da etiqueta: "FULL" (padrão) ou "COMPACT" (compacta 50x30mm). */
  format?: LabelFormat;
}

export interface OrderLabelPreviewItem {
  productId: string;
  productName: string;
  unit: string;
  orderQuantity: number;
  suggestedLabelCount: number;
  netWeightGrams: number | null;
  portionSizeGrams: number | null;
  lote: string;
  fabricadoEm: string; // yyyy-MM-dd
  validoAte: string | null; // yyyy-MM-dd
  hasLabelInfo: boolean;
  missingFields: string[];
}

export interface OrderLabelPreview {
  orderId: string;
  orderNumber: number;
  clientName: string;
  items: OrderLabelPreviewItem[];
}

/** POST que retorna um PDF e dispara o download no navegador (impressao manual na Argox). */
async function downloadLabelPdf(path: string, body: unknown): Promise<void> {
  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    let message = "Falha ao gerar etiqueta";
    try {
      const data = await response.json();
      message = data?.message ?? message;
    } catch {
      // resposta nao-JSON
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const match = disposition.match(/filename="?([^";]+)"?/i);
  const filename = match?.[1] ?? "etiqueta.pdf";

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * Gera o PDF de etiquetas de um unico produto e dispara o download.
 */
export async function generateLabelPdf(input: LabelGenerateInput): Promise<void> {
  await downloadLabelPdf("/api/v1/labels/generate", input);
}

/** Busca a pre-visualizacao das etiquetas de um pedido (defaults + campos faltantes). */
export async function fetchOrderLabelPreview(orderId: string): Promise<OrderLabelPreview> {
  return requestJson<OrderLabelPreview>(`/api/v1/labels/order/${orderId}/preview`);
}

/** Gera um unico PDF com todas as etiquetas confirmadas do pedido e dispara o download. */
export async function generateLabelBatchPdf(labels: LabelGenerateInput[]): Promise<void> {
  await downloadLabelPdf("/api/v1/labels/batch", { labels });
}
